import React, { useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { unsubscribe } from '../api';
import { showToast } from '../toaster';

const reasons = [
  'I do not understand the amount on my bank statement',
  'I wish to receive the invoice of my order',
  'I wish to cancel',
  'I want to unsubscribe',
  'Other',
];

export default function ContactForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [iban, setIban] = useState('');
  const [reasonIdx, setReasonIdx] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setIban('');
    setReasonIdx(0);
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const isUnsub = reasonIdx === 2 || reasonIdx === 3;
    try {
      const result = await unsubscribe({
        first_name: firstName,
        last_name: lastName,
        email,
        iban_last_5: iban,
        is_unsubscribe: isUnsub,
        message,
        options: { reason: reasons[reasonIdx] },
      });
      showToast(
        isUnsub ? 'Unsubscribe request sent successfully.' : 'Message sent successfully.',
        'success'
      );
      reset();
    } catch (err: any) {
      let msg = err?.message || 'Something went wrong.';
      // If error is JSON, try to extract message
      try {
        const parsed = JSON.parse(msg);
        if (parsed && typeof parsed.message === 'string') msg = parsed.message;
      } catch {}
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div  className="w-full overflow-hidden rounded-[1.5rem] border border-[#f6c7b4] bg-white shadow-[0_30px_80px_-44px_rgba(13,27,42,0.55)]">
      <div className="border-b border-[#f6c7b4] bg-gradient-to-r from-[#102a43] via-[#1f4f7f] to-[#2b6cb0] px-5 py-4 text-white sm:px-6">
        <h3 className="text-base font-bold sm:text-lg">Contact us</h3>
        <p className="mt-1 text-sm text-[#dceafb]">Our operators will answer your questions quickly and efficiently.</p>
      </div>
      <form className="space-y-3.5 p-5 text-sm sm:p-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <input
            aria-label="First name"
            placeholder="First name *"
            className="w-full rounded-xl border border-[#d7e5f3] bg-[#f7fbff] px-3 py-2.5 text-[#27425d] transition focus:border-[#2b6cb0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#dceafb]"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
          />
          <input
            aria-label="Last name"
            placeholder="Last name *"
            className="w-full rounded-xl border border-[#d7e5f3] bg-[#f7fbff] px-3 py-2.5 text-[#27425d] transition focus:border-[#2b6cb0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#dceafb]"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            required
          />
        </div>
        <input
          type="email"
          aria-label="Email"
          placeholder="Email *"
          className="w-full rounded-xl border border-[#d7e5f3] bg-[#f7fbff] px-3 py-2.5 text-[#27425d] transition focus:border-[#2b6cb0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#dceafb]"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        <input
          aria-label="IBAN"
          placeholder="Last 5 digits of IBAN *"
          className="w-full rounded-xl border border-[#d7e5f3] bg-[#f7fbff] px-3 py-2.5 text-[#27425d] transition focus:border-[#2b6cb0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#dceafb]"
          value={iban}
          onChange={e => setIban(e.target.value)}
          required
          maxLength={5}
        />
        <select
          className="w-full rounded-xl border border-[#d7e5f3] bg-[#f7fbff] px-3 py-2.5 text-[#27425d] transition focus:border-[#2b6cb0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#dceafb]"
          value={reasonIdx}
          onChange={e => setReasonIdx(Number(e.target.value))}
        >
          {reasons.map((r, i) => (
            <option value={i} key={r}>{`${i + 1} - ${r}`}</option>
          ))}
        </select>
        <textarea
          aria-label="Message"
          placeholder="Your message *"
          className="h-24 w-full resize-none rounded-xl border border-[#d7e5f3] bg-[#f7fbff] px-3 py-3 text-[#27425d] transition focus:border-[#2b6cb0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#dceafb]"
          value={message}
          onChange={e => setMessage(e.target.value)}
          required
        />
        <button
          type="submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--brand-accent)] py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#df5532] disabled:opacity-60"
          disabled={loading}
        >
          <FiSend className="w-4 h-4" /> {loading ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </form>
    </div>
  );
}